import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { getApiUrl } from '../../config/api';
import './BotsDashboard.css';

interface Bot {
  id: string;
  user_id: string;
  credential_id: string;
  strategy_type: string;
  exchange: string;
  symbol: string;
  scan_mode: boolean;
  scan_config?: any;
  status: string;
  config: any;
  error_message?: string;
  started_at?: string;
  stopped_at?: string;
  created_at: string;
  updated_at: string;
}

interface BotSymbols {
  scan_mode: boolean;
  symbols: string[];
  count?: number;
}

const BotsDashboard: React.FC = () => {
  const { t } = useTranslation();
  const [bots, setBots] = useState<Bot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [botSymbols, setBotSymbols] = useState<Record<string, BotSymbols>>({});
  const [expandedBot, setExpandedBot] = useState<string | null>(null);

  useEffect(() => {
    fetchBots();
  }, []);

  const fetchBots = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(getApiUrl('/api/bots'), {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch bots: ${response.status}`);
      }

      const data = await response.json();
      setBots(data || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load bots');
      console.error('Error fetching bots:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch active symbols for scanner bots
  const fetchSymbols = async (botId: string) => {
    try {
      const response = await fetch(getApiUrl(`/api/bots/${botId}/symbols`), {
        credentials: 'include',
      });
      if (response.ok) {
        const data: BotSymbols = await response.json();
        setBotSymbols((prev) => ({ ...prev, [botId]: data }));
      }
    } catch (err) {
      console.error('Error fetching symbols:', err);
    }
  };

  // Auto-fetch symbols for running scanner bots
  useEffect(() => {
    const scannerBots = bots.filter((b) => b.scan_mode && (b.status === 'running' || b.status === 'paused'));
    scannerBots.forEach((b) => fetchSymbols(b.id));

    if (scannerBots.length > 0) {
      const interval = setInterval(() => {
        scannerBots.forEach((b) => fetchSymbols(b.id));
      }, 30000); // refresh every 30s
      return () => clearInterval(interval);
    }
  }, [bots]);

  const handleAction = async (botId: string, action: 'start' | 'pause' | 'stop') => {
    try {
      setActionLoading(botId);
      const response = await fetch(getApiUrl(`/api/bots/${botId}/${action}`), {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to ${action} bot`);
      }

      // Refresh bots list
      await fetchBots();
    } catch (err: any) {
      setError(err.message || `Failed to ${action} bot`);
      console.error(`Error ${action} bot:`, err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (botId: string) => {
    if (!window.confirm('Are you sure you want to delete this bot?')) {
      return;
    }

    try {
      setActionLoading(botId);
      const response = await fetch(getApiUrl(`/api/bots/${botId}`), {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete bot');
      }

      // Refresh bots list
      await fetchBots();
    } catch (err: any) {
      setError(err.message || 'Failed to delete bot');
      console.error('Error deleting bot:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const getStrategyBadgeClass = (strategyType: string): string => {
    const baseClass = 'strategy-badge';
    switch (strategyType) {
      case 'volume_breakout':
        return `${baseClass} strategy-volume`;
      case 'ob_imbalance':
        return `${baseClass} strategy-ob`;
      case 'momentum':
        return `${baseClass} strategy-momentum`;
      case 'funding_rate':
        return `${baseClass} strategy-funding`;
      default:
        return baseClass;
    }
  };

  const getStrategyDisplayName = (strategyType: string): string => {
    const strategyNames: Record<string, string> = {
      volume_breakout: 'Volume Breakout',
      ob_imbalance: 'OB Imbalance',
      momentum: 'Momentum Ignition',
      funding_rate: 'Funding Rate',
    };
    return strategyNames[strategyType] || strategyType;
  };

  const getStatusClass = (status: string): string => {
    switch (status) {
      case 'running':
        return 'status-running';
      case 'paused':
        return 'status-paused';
      case 'error':
        return 'status-error';
      default:
        return 'status-stopped';
    }
  };

  const getStatusDisplayName = (status: string): string => {
    const statusNames: Record<string, string> = {
      created: 'Created',
      running: 'Running',
      paused: 'Paused',
      stopped: 'Stopped',
      error: 'Error',
    };
    return statusNames[status] || status;
  };

  const canDelete = (status: string): boolean => {
    return ['stopped', 'created', 'error'].includes(status);
  };

  const activeBots = bots.filter((b) => b.status === 'running');
  const stoppedBots = bots.filter((b) => ['stopped', 'created'].includes(b.status));

  if (loading) {
    return (
      <div className="max-w-[1400px] mx-auto px-5 pt-20 pb-24">
        <div className="text-viz-text-secondary text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-5 pt-20 pb-24">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-viz-text-primary text-3xl font-bold">Bots</h1>
        <Link
          to="/bots/new"
          className="bg-viz-accent hover:bg-viz-accent-hover text-white rounded-lg px-5 py-2.5 font-medium transition-all duration-200 shadow-glow hover:shadow-glow-lg"
        >
          Create Bot
        </Link>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-viz-error-bg border border-viz-error rounded-lg text-viz-error text-sm">
          {error}
        </div>
      )}

      {/* Summary Bar */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-viz-bg-secondary border border-viz-border rounded-xl p-5 backdrop-blur-glass shadow-card">
          <div className="text-viz-text-secondary text-sm mb-1">Total Bots</div>
          <div className="text-viz-text-primary text-2xl font-bold">{bots.length}</div>
        </div>
        <div className="bg-viz-bg-secondary border border-viz-border rounded-xl p-5 backdrop-blur-glass shadow-card">
          <div className="text-viz-text-secondary text-sm mb-1">Active Bots</div>
          <div className="text-viz-success text-2xl font-bold">{activeBots.length}</div>
        </div>
        <div className="bg-viz-bg-secondary border border-viz-border rounded-xl p-5 backdrop-blur-glass shadow-card">
          <div className="text-viz-text-secondary text-sm mb-1">Stopped Bots</div>
          <div className="text-viz-text-secondary text-2xl font-bold">{stoppedBots.length}</div>
        </div>
      </div>

      {/* Bots Grid */}
      {bots.length === 0 ? (
        <div className="bg-viz-bg-secondary border border-viz-border rounded-xl p-12 text-center backdrop-blur-glass shadow-card">
          <div className="text-viz-text-secondary mb-4 text-lg">No bots yet</div>
          <Link
            to="/bots/new"
            className="inline-block text-viz-accent hover:text-viz-accent-hover transition-colors duration-200 font-medium"
          >
            Create your first bot
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {bots.map((bot) => (
            <div
              key={bot.id}
              className="bg-viz-bg-secondary border border-viz-border rounded-xl p-6 backdrop-blur-glass shadow-card hover:border-viz-border-hover transition-all duration-200"
            >
              {/* Bot Header */}
              <Link to={`/bots/${bot.id}`} className="block mb-4 group">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-viz-text-primary text-xl font-bold mb-1 group-hover:text-viz-accent transition-colors">
                      {bot.scan_mode ? 'Auto Scanner' : bot.symbol}
                    </h3>
                    <div className="text-viz-text-secondary text-xs uppercase tracking-wide">
                      {bot.exchange}
                    </div>
                  </div>
                  <div className={`status-led ${getStatusClass(bot.status)}`} title={getStatusDisplayName(bot.status)} />
                </div>
              </Link>

              {/* Strategy Badge + Scanner Badge */}
              <div className="mb-4 flex flex-wrap gap-2">
                <span className={getStrategyBadgeClass(bot.strategy_type)}>
                  {getStrategyDisplayName(bot.strategy_type)}
                </span>
                {bot.scan_mode && (
                  <span className="strategy-badge strategy-scanner">
                    Scanner {botSymbols[bot.id]?.count ? `(${botSymbols[bot.id].count})` : ''}
                  </span>
                )}
              </div>

              {/* Scanner Symbols */}
              {bot.scan_mode && botSymbols[bot.id]?.symbols?.length > 0 && (
                <div className="mb-4">
                  <div
                    className="flex items-center justify-between cursor-pointer text-sm text-viz-text-secondary hover:text-viz-text-primary transition-colors"
                    onClick={() => setExpandedBot(expandedBot === bot.id ? null : bot.id)}
                  >
                    <span>Active Symbols ({botSymbols[bot.id].symbols.length})</span>
                    <span className="text-xs">{expandedBot === bot.id ? '▲' : '▼'}</span>
                  </div>
                  {expandedBot === bot.id && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {botSymbols[bot.id].symbols.sort().map((sym) => (
                        <span
                          key={sym}
                          className="inline-block px-2 py-0.5 text-[10px] font-medium rounded bg-white/5 border border-white/10 text-viz-text-secondary"
                        >
                          {sym.replace('USDT', '')}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Status Info */}
              <div className="mb-4 pb-4 border-b border-viz-border">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-viz-text-secondary">Status:</span>
                  <span className={`font-medium ${getStatusClass(bot.status)}`}>
                    {getStatusDisplayName(bot.status)}
                  </span>
                </div>
                {bot.error_message && (
                  <div className="mt-2 text-viz-error text-xs">
                    {bot.error_message}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                {bot.status === 'created' || bot.status === 'stopped' || bot.status === 'paused' ? (
                  <button
                    onClick={() => handleAction(bot.id, 'start')}
                    disabled={actionLoading === bot.id}
                    className="flex-1 bg-viz-success hover:bg-viz-success/80 text-white rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {actionLoading === bot.id ? 'Starting...' : 'Start'}
                  </button>
                ) : null}

                {bot.status === 'running' ? (
                  <>
                    <button
                      onClick={() => handleAction(bot.id, 'pause')}
                      disabled={actionLoading === bot.id}
                      className="flex-1 bg-viz-warning hover:bg-viz-warning/80 text-white rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {actionLoading === bot.id ? 'Pausing...' : 'Pause'}
                    </button>
                    <button
                      onClick={() => handleAction(bot.id, 'stop')}
                      disabled={actionLoading === bot.id}
                      className="flex-1 bg-viz-error hover:bg-viz-error/80 text-white rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {actionLoading === bot.id ? 'Stopping...' : 'Stop'}
                    </button>
                  </>
                ) : null}

                {bot.status === 'paused' ? (
                  <button
                    onClick={() => handleAction(bot.id, 'stop')}
                    disabled={actionLoading === bot.id}
                    className="flex-1 bg-viz-error hover:bg-viz-error/80 text-white rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {actionLoading === bot.id ? 'Stopping...' : 'Stop'}
                  </button>
                ) : null}

                <Link
                  to={`/bots/${bot.id}`}
                  className="bg-viz-accent/20 hover:bg-viz-accent/30 border border-viz-accent/40 hover:border-viz-accent text-viz-accent rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200"
                  title="View Charts"
                >
                  Charts
                </Link>

                {canDelete(bot.status) && (
                  <button
                    onClick={() => handleDelete(bot.id)}
                    disabled={actionLoading === bot.id}
                    className="bg-viz-bg-tertiary hover:bg-viz-error/20 border border-viz-border hover:border-viz-error text-viz-error rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Delete Bot"
                  >
                    {actionLoading === bot.id ? '...' : '🗑️'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BotsDashboard;
